<template>
  <div class="newton-third-law-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F₁₂ = -F₂₁$</div>
    </div>

    <div class="canvas-box">
      <svg width="100%" height="250" viewBox="0 0 400 250">
        <!-- 轨道 -->
        <line x1="20" y1="200" x2="380" y2="200" stroke="#333" stroke-width="2" />
        
        <!-- 球 A -->
        <g :transform="`translate(${p1.x}, 200)`">
          <circle :r="p1.mass * 10" fill="#ff4757" />
          <text :y="-p1.mass * 10 - 5" text-anchor="middle" fill="#ff4757" font-size="10">m1={{ p1.mass }}</text>
        </g>

        <!-- 球 B -->
        <g :transform="`translate(${p2.x}, 200)`">
          <circle :r="p2.mass * 10" fill="#00d2ff" />
          <text :y="-p2.mass * 10 - 5" text-anchor="middle" fill="#00d2ff" font-size="10">m2={{ p2.mass }}</text>
        </g>

        <!-- 碰撞时的力矢量 -->
        <g v-if="isColliding" transform="translate(200, 200)">
           <!-- 炸开效果 -->
           <circle r="30" fill="url(#burstGrad)" opacity="0.6" />
           
           <!-- F1 (B对A的力) -->
           <line x1="0" y1="0" x2="-60" y2="0" stroke="#ff4757" stroke-width="4" marker-end="url(#arr-f1)" />
           <text x="-50" y="-15" fill="#ff4757" font-size="12" font-weight="bold">F21</text>
           
           <!-- F2 (A对B的力) -->
           <line x1="0" y1="0" x2="60" y2="0" stroke="#00d2ff" stroke-width="4" marker-end="url(#arr-f2)" />
           <text x="35" y="-15" fill="#00d2ff" font-size="12" font-weight="bold">F12</text>
        </g>
      </svg>
    </div>

    <div class="controls">
      <div class="slider-row">
        <span>修改质量 m1:</span>
        <el-slider v-model="p1.mass" :min="1" :max="4" :step="0.5" size="mini"></el-slider>
      </div>
      <div class="btn-row">
        <el-button type="primary" size="mini" @click="start">开始对抗碰撞</el-button>
        <el-button size="mini" @click="reset">重置</el-button>
      </div>
      <p class="desc">{{ theoryTip }}</p>
    </div>

    <defs>
      <radialGradient id="burstGrad">
        <stop offset="0%" stop-color="#fff" />
        <stop offset="50%" stop-color="#ffff00" />
        <stop offset="100%" stop-color="transparent" />
      </radialGradient>
      <marker id="arr-f1" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"> <polygon points="0 0, 8 3, 0 6" fill="#ff4757" /> </marker>
      <marker id="arr-f2" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"> <polygon points="0 0, 8 3, 0 6" fill="#00d2ff" /> </marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'NewtonThirdLawViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      p1: { x: 50, v: 3, mass: 1 },
      p2: { x: 350, v: -3, mass: 2 },
      isMoving: false,
      isColliding: false,
      running: true
    }
  },
  computed: {
    displayTitle() {
      return this.knowledgePoint || '牛顿第三定律：作用力与反作用力';
    },
    theoryTip() {
      let tip = '无论质量差异多大，相互作用力始终大小相等、方向相反。';
      if (this.knowledgePoint && this.knowledgePoint.includes('三要素')) {
        tip = '力的效果由【三要素】决定：大小、方向、作用点。在相互作用中，两者方向相反，作用点分别在不同物体上。';
      }
      return tip;
    }
  },
  methods: {
    start() {
      this.reset();
      this.isMoving = true;
      this.animate();
    },
    reset() {
      this.p1.x = 50;
      this.p1.v = 3;
      this.p2.x = 350;
      this.p2.v = -3;
      this.isMoving = false;
      this.isColliding = false;
    },
    animate() {
      if (!this.isMoving || !this.running) return;
      
      this.p1.x += this.p1.v;
      this.p2.x += this.p2.v;

      const distance = this.p2.x - this.p1.x;
      const minDistance = (this.p1.mass + this.p2.mass) * 10;

      if (distance <= minDistance) {
        this.isMoving = false;
        this.isColliding = true;
        this.p1.x = 200 - minDistance/2;
        this.p2.x = 200 + minDistance/2;
      } else {
        requestAnimationFrame(this.animate);
      }
    }
  },
  beforeDestroy() {
    this.running = false;
  }
}
</script>

<style scoped lang="scss">
.newton-third-law-viz {
  width: 100%;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.viz-header {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  .title { color: #00d2ff; font-weight: bold; }
  .stats { font-size: 0.8rem; color: #aaa; }
}
.canvas-box { background: #0a0a0a; border-radius: 8px; overflow: hidden; }
.controls {
  padding: 15px;
  background: rgba(255, 71, 87, 0.05);
  border-radius: 8px;
  .slider-row { display: flex; align-items: center; gap: 15px; span { font-size: 0.85rem; } .el-slider { flex: 1; } }
  .btn-row { display: flex; gap: 10px; margin-top: 10px; }
  .desc { font-size: 0.8rem; color: #888; margin-top: 10px; text-align: center; }
}
</style>
